/**
 * "What am I actually running?" — the answer, on screen (Card 13).
 *
 * WHY THIS EXISTS. Once OTA is live, the app's version stops being one number. A device carries
 * a NATIVE version (the binary from the store / TestFlight / the R2 channel) and, independently,
 * a WEB BUNDLE version that arrived over the air. Those move on different schedules, and a bug
 * report is close to useless without both — "0.0.36" alone cannot distinguish the bundle that
 * shipped inside the binary from an OTA bundle applied ten minutes ago.
 *
 * Before this, nothing in the UI reported either one, so confirming an OTA update meant guessing
 * from behaviour. Now you ask the tester to read one line.
 *
 * `bundle.version` is the literal string `"builtin"` when the running web layer is the one
 * compiled into the binary — that is Capgo's own sentinel, not a placeholder we invented, and it
 * is exactly what distinguishes "no OTA applied" from "OTA applied".
 *
 * Degrades quietly rather than disappearing: on web there is no native shell, and on a native
 * build predating Card 13 the updater plugin is absent and `current()` rejects as
 * "unimplemented". Both cases still render whatever is known.
 */

import { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNative } from '@/utils/platform';

interface VersionState {
  /** Native versionName + build, e.g. "0.0.36 (138)". Absent on web. */
  native?: string;
  /** Running OTA bundle version, or "builtin" when it is the one compiled in. */
  bundle?: string;
}

export function AppVersionFooter() {
  const [version, setVersion] = useState<VersionState>({});

  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;

    (async () => {
      const next: VersionState = {};

      try {
        const info = await App.getInfo();
        next.native = `${info.version} (${info.build})`;
      } catch {
        // Non-fatal: the line just omits the native half.
      }

      try {
        const current = await CapacitorUpdater.current();
        next.bundle = current.bundle.version;
      } catch {
        // The updater is not in this binary — i.e. a build older than 0.0.36. Leaving `bundle`
        // undefined is the honest answer: this install cannot receive OTA at all.
      }

      if (!cancelled) setVersion(next);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!version.native && !version.bundle) return null;

  return (
    <p className="mt-8 mb-2 text-center text-xs text-gray-400" data-testid="app-version-footer">
      Trade Loop
      {version.native ? ` ${version.native}` : ''}
      {version.bundle ? ` · web ${version.bundle}` : ''}
    </p>
  );
}
